import { Card, Col, Form, message, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getSingleCandidateDetails, updateCandidateStatusAsync } from "../../../Slices/hiringSlice";
import { AntdButton, SelectInput, TextareaField } from "../../../blueplanitUtils/InputField";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import dayjs from "dayjs";

const { Title } = Typography;

const CandidateDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const candidate = useSelector((state) => state.hiring.singleCandidate);

    const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

    const [comment, setComment] = useState("");
    const [stage, setStage] = useState("");
    const [status, setStatus] = useState("")

    useEffect(() => {
        if (id) {
            dispatch(getSingleCandidateDetails(id))
        }
    }, [dispatch, id])

    useEffect(() => {
        if (candidate) {
            setStatus(candidate.status || "");
            setStage(candidate.stage || "");
            setComment(candidate.comments || ""); // ✅ always string
        }
    }, [candidate]);


    const handleBack = () => {
        navigate(-1)
    }

    const changedFields = [
        'status', 'stage', 'comments',"approvalStatus"
    ];

    const formatValue = (value) => {
        if (typeof value === 'string' && value.length > 40) {
            return `${value.substring(0, 40)}...`;
        }
        return value || '—';
    };

    const getStatus = (createdAt, editedAt, deletedAt) => {
    if (deletedAt) {
        return 'Deleted';
    }

    if (editedAt && editedAt !== createdAt) {
        return 'Updated';
    }

    if (createdAt) {
        return 'Created';
    }

    return '—';
};


    const columns = [
        {
            title: 'User Name',
            dataIndex: 'editedBy',
            key: 'editedBy'
        },
        {
            title: 'Updated Time',
            key: 'editedAt',
            render: (_,record) => (
                record.editedAt ? new Date(Number(record.editedAt)).toLocaleString() : record.createdAt ? new Date(Number(record.createdAt)).toLocaleString() : '—'
            ),
        },  
        {
            title: 'Action',
             key: 'action',
            width: 120,
            render: (_, record) =>
        getStatus(
            record.createdAt,
            record.editedAt,
            record.deletedAt
        ),
        },
        ...changedFields.map((field) => ({
                   title: field.charAt(0).toUpperCase() + field.slice(1),
                   key: field,
                   width: 160,
                   render: (_, record) => {
                       const change = record?.changes?.find((c) => (
                           c.field === ""
                               ? c.field === field && dayjs(c.oldValue).format('DD/MM/YYYY') && dayjs(c.newValue).format('DD/MM/YYYY')
                               : c.field === field
                       )
                       );
                       if (!change || change.oldValue === change.newValue || change.oldValue === 'No change') {
                           return <span>No Change</span>;
                       }
                       return (
                           <div>
                               <span>{formatValue(change.oldValue)}</span>
                               <span style={{ margin: '0 4px' }}>→</span>
                               <span>{formatValue(change.newValue)}</span>
                           </div>
                       );
                   },
               }))
    ];

    const scheduleInterview = async () => {
        if (!stage) {
            message.error("Please select candidate stage.");
            return;
        }
        if (!status || status !== "Scheduled") {
            message.error("Please select 'Scheduled' as status.");
            return;
        }

        try {
            const payload = {
                candidateId: candidate.candidateId,
                stage,
                status,
                comments: comment,
                editedBy: loginUser.firstname + " " + loginUser.lastname,
            };

            await dispatch(updateCandidateStatusAsync(payload)).unwrap();
            message.success(`Interview scheduled for ${stage} stage!`);
        } catch (err) {
            console.error(err);
            message.error("Failed to schedule interview.");
        }
    };


    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <div className="mb-2">
                    <AntdButton
                        label="Back"
                        type="primary"
                        onClick={handleBack}
                    />
                </div>
                <Card style={{ padding: "20px" }}>
                    <Row gutter={24}>

                        <Col span={6} style={{ textAlign: "center" }}>
                            <img
                                src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                alt="Profile"
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: "50%",
                                    border: "3px solid #1677ff",
                                    objectFit: "cover",
                                    marginBottom: 15
                                }}
                            />
                        </Col>

                        <Col span={18}>
                            <h2 style={{ fontWeight: "600", fontSize: 22, marginBottom: 15 }}>
                                {candidate.firstName} {candidate.lastName}
                            </h2>

                            <Row style={{ fontSize: 16 }} gutter={[16, 10]}>
                                <Col span={12}><b>Candidate ID:</b> {candidate.candidateId}</Col>
                                <Col span={12}><b>Email:</b> {candidate.email}</Col>
                                <Col span={12}><b>Phone:</b> {candidate.phone}</Col>
                                <Col span={12}><b>Department:</b> {candidate.department}</Col>
                                <Col span={12}><b>Experience:</b> {candidate.experience}</Col>
                                <Col span={12}><b>Current CTC:</b> {candidate.currentCTC}</Col>
                                <Col span={12}><b>Expected CTC:</b> {candidate.expectedCTC}</Col>
                                <Col span={12}><b>Applying For:</b> {candidate.jobTitle}</Col>
                                <Col span={12}><b>Source:</b> {candidate.source}</Col>
                                <Col span={12}><b>Status:</b> {candidate.approvalStatus}</Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row gutter={24} style={{ marginTop: "20px" }}>
                        <Col span={6}>
                            <SelectInput
                                label="Select Stage"
                                placeholder="Select Stage"
                                style={{ width: "100%" }}
                                value={stage}
                                options={[
                                    { label: "Screening", value: "Screening" },
                                    { label: "Technical Round", value: "Technical" },
                                    { label: "HR Round", value: "HR" },
                                    { label: "Manager Round", value: "Manager" }
                                ]}
                                onChange={setStage}
                            />
                        </Col>
                        <Col span={6}>
                            <SelectInput
                                label="Select Status"
                                placeholder="Select Status"
                                style={{ width: "100%" }}
                                value={status}
                                options={[
                                    { label: "Scheduled", value: "Scheduled" },
                                    { label: "Hired", value: "Hired" },
                                    { label: "Rejected", value: "Rejected" }
                                ]}
                                onChange={setStatus}
                            />

                        </Col>
                        <Col span={6}>
                            <TextareaField
                                label="Add Comments"
                                value={comment}              // ✅ controlled
                                onChange={(e) => setComment(e.target.value)}
                                rows={3}
                            />

                        </Col>
                        <Col span={6}>
                            <Typography>Actions</Typography>
                            <AntdButton
                                label="Schedule Interview"
                                type="primary"
                                onClick={scheduleInterview}
                            />
                        </Col>
                    </Row>
                </Card>

                <Card style={{ marginTop: 20, padding: 20 }}>
                    <Row gutter={24}>

                        <Col span={12} style={{ marginBottom: "20px" }}>
                            <h3 style={{ fontWeight: 600, marginBottom: 15 }}>Resume</h3>

                            {candidate.resumeUrl ? (
                                <>
                                    <div
                                        style={{
                                            padding: "10px 15px",
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 6,
                                            background: "#fafafa",
                                            marginBottom: 15
                                        }}
                                    >
                                        <b>Uploaded Resume:</b> {candidate.resumeFilename}
                                    </div>

                                    <Space>
                                        <a
                                            href={candidate.resumeUrl}
                                            target="_blank"
                                        >
                                            <AntdButton
                                                label="View Resume"
                                                type="primary"
                                            />
                                        </a>
                                        <a
                                            href={candidate.resumeUrl}
                                            download={candidate.resumeFilename}
                                        >
                                            <AntdButton
                                                label="Download"
                                            />
                                        </a>
                                    </Space>
                                </>
                            ) : (
                                <div style={{ color: "#888" }}>No resume uploaded</div>
                            )}
                        </Col>
                        <Col span={12} style={{marginTop:"5px"}}>
                            <h4 style={{ fontWeight: 600, marginBottom: 15 }}>Questions & Answers</h4>
                            {candidate.answers && Object.keys(candidate.answers).length > 0 ? (
                                <div>
                                    {Object.entries(candidate.answers).map(([question, answer], index) => (
                                        <div key={index} style={{ marginBottom: 10 }}>
                                            <p style={{ fontWeight: 500, marginBottom: 2 }}>{index + 1}. {question}</p>
                                            <p style={{ marginLeft: 10, color: '#555' }}>{answer || "No answer provided"}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No answers submitted yet.</p>
                            )}
                        </Col>

                        <Col span={12}>
                            <h4 style={{ fontWeight: 600, marginBottom: 15 }}>
                                Exam Questions & Answers
                            </h4>

                            {candidate.exam && candidate.exam.answers?.length > 0 ? (
                                <div>
                                    <p style={{ fontWeight: 500, marginBottom: 10 }}>
                                        Exam ID: {candidate.exam.examId}
                                    </p>

                                    {candidate.exam.answers.map((item, index) => (
                                        <div key={index} style={{ marginBottom: 10 }}>
                                            <p style={{ fontWeight: 500, marginBottom: 2 }}>
                                                {index + 1}. {item.question}
                                            </p>
                                            <p style={{ marginLeft: 10, color: "#555" }}>
                                                {item.answer || "No answer provided"}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No exam answers submitted yet.</p>
                            )}
                        </Col>


                    </Row>
                </Card>

                <Card style={{ marginTop: 20, padding: 20 }}>
                    <Title level={4} style={{ marginBottom: 16 }}>Candidate Update History</Title>
                    <CustomGridTable
                    columns={columns}
                    dataSource={candidate.updateHistory || []}
                    />
                </Card>
            </div>
        </div>
    )

};

export default CandidateDetails;